import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TodoDayDto, TurnInAssignmentDto, TurnInSubjectDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import form from './settingsForm.module.css';
import styles from './PlannerSettings.module.css';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Monday (local) of the week containing `d`. */
function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0=Sun…6=Sat
  x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow));
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The 4–8 weekly planner editor: manage the Turn-In subject list, and fill in
 * each day's Turn-In assignments + To-Do list a week at a time. Only rendered
 * when the classroom's grade range is 4–8.
 */
export function PlannerSettings({ classroomId }: { classroomId: string }) {
  const api = useApi();
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const weekStartIso = isoOf(weekStart);
  const weekEnd = addDays(weekStart, 4);
  const weekEndIso = isoOf(weekEnd);
  const dates = WEEKDAYS.map((_, i) => addDays(weekStart, i));

  const subjects = useQuery<TurnInSubjectDto[]>({
    queryKey: ['planner-subjects', classroomId],
    queryFn: () => api.planner.listSubjects(classroomId),
  });
  const assignments = useQuery<TurnInAssignmentDto[]>({
    queryKey: ['planner-assignments', classroomId, weekStartIso],
    queryFn: () => api.planner.listAssignments(classroomId, weekStartIso, weekEndIso),
  });
  const todo = useQuery<TodoDayDto[]>({
    queryKey: ['planner-todo', classroomId, weekStartIso],
    queryFn: () => api.planner.listTodo(classroomId, weekStartIso, weekEndIso),
  });

  const subjectList = subjects.data ?? [];

  return (
    <div className={styles.planner}>
      <SubjectsManager classroomId={classroomId} subjects={subjectList} />

      <div className={form.section}>
        <div className={styles.weekHead}>
          <h3 className={form.sectionTitle}>Weekly planner</h3>
          <div className={styles.weekNav}>
            <button
              type="button"
              className={form.buttonGhost}
              onClick={() => setWeekStart((w) => addDays(w, -7))}
              aria-label="Previous week"
            >
              ‹
            </button>
            <button
              type="button"
              className={form.button}
              onClick={() => setWeekStart(mondayOf(new Date()))}
            >
              This week
            </button>
            <button
              type="button"
              className={form.buttonGhost}
              onClick={() => setWeekStart((w) => addDays(w, 7))}
              aria-label="Next week"
            >
              ›
            </button>
          </div>
        </div>
        <p className={form.hint}>
          {shortDate(weekStart)} – {shortDate(weekEnd)}. Fill in each subject&apos;s assignment and
          the day&apos;s to-do list; the live screen shows the right day automatically.
        </p>

        {subjectList.length === 0 ? (
          <p className={form.empty}>Add a subject above to start planning Turn-In assignments.</p>
        ) : (
          <div className={styles.gridScroll}>
            <div
              className={styles.grid}
              style={{ gridTemplateColumns: `minmax(120px, 1fr) repeat(5, minmax(140px, 1fr))` }}
            >
              {/* Header row */}
              <div className={`${styles.cell} ${styles.cornerCell}`}>Turn In</div>
              {dates.map((d, i) => (
                <div key={i} className={`${styles.cell} ${styles.dayHead}`}>
                  <span className={styles.dayName}>{WEEKDAYS[i]}</span>
                  <span className={styles.dayDate}>{shortDate(d)}</span>
                </div>
              ))}

              {/* Subject rows */}
              {subjectList.map((s) => (
                <SubjectRow
                  key={s.id}
                  classroomId={classroomId}
                  weekStartIso={weekStartIso}
                  subject={s}
                  dates={dates}
                  assignments={assignments.data ?? []}
                />
              ))}

              {/* To-Do row */}
              <div className={`${styles.cell} ${styles.rowLabel} ${styles.todoLabel}`}>To-Do</div>
              {dates.map((d, i) => (
                <TodoCell
                  key={i}
                  classroomId={classroomId}
                  weekStartIso={weekStartIso}
                  dateIso={isoOf(d)}
                  days={todo.data ?? []}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Subjects manager ----------

function SubjectsManager({
  classroomId,
  subjects,
}: {
  classroomId: string;
  subjects: TurnInSubjectDto[];
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['planner-subjects', classroomId] });

  const add = useMutation({
    mutationFn: (n: string) => api.planner.createSubject(classroomId, n),
    onSuccess: () => {
      invalidate();
      setName('');
    },
  });
  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api.planner.renameSubject(classroomId, v.id, v.name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.planner.removeSubject(classroomId, id),
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => api.planner.reorderSubjects(classroomId, orderedIds),
    onSuccess: invalidate,
  });

  const move = (index: number, dir: -1 | 1) => {
    const ids = subjects.map((s) => s.id);
    const target = index + dir;
    if (target < 0 || target >= ids.length) return;
    const moved = ids[index];
    if (moved === undefined) return;
    ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorder.mutate(ids);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed) add.mutate(trimmed);
  };

  return (
    <div className={form.section}>
      <h3 className={form.sectionTitle}>Turn-In subjects</h3>
      <p className={form.hint}>
        The subjects shown down the side of the Turn-In box (Spelling, Math, Bible…). Reorder with
        <span aria-hidden="true"> ‹ / › </span>; remove with ✕.
      </p>

      {subjects.length === 0 ? (
        <p className={form.empty}>No subjects yet — add your first below.</p>
      ) : (
        <ul className={form.list}>
          {subjects.map((s, i) => (
            <li key={s.id} className={form.listItem}>
              <input
                key={`${s.id}:${s.name}`}
                className={`${form.input} ${styles.subjectInput}`}
                defaultValue={s.name}
                maxLength={60}
                aria-label={`Rename ${s.name}`}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== s.name) rename.mutate({ id: s.id, name: v });
                  else e.target.value = s.name;
                }}
              />
              <div className={styles.subjectControls}>
                <button
                  type="button"
                  className={form.buttonGhost}
                  disabled={i === 0 || reorder.isPending}
                  onClick={() => move(i, -1)}
                  aria-label={`Move ${s.name} up`}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={form.buttonGhost}
                  disabled={i === subjects.length - 1 || reorder.isPending}
                  onClick={() => move(i, 1)}
                  aria-label={`Move ${s.name} down`}
                >
                  ›
                </button>
                <button
                  type="button"
                  className={form.buttonGhost}
                  disabled={remove.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove "${s.name}"? Its saved assignments will be deleted too.`,
                      )
                    ) {
                      remove.mutate(s.id);
                    }
                  }}
                  aria-label={`Remove ${s.name}`}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form className={form.formRow} onSubmit={onSubmit}>
        <div className={form.field}>
          <label className={form.label} htmlFor="new-subject">
            New subject
          </label>
          <input
            id="new-subject"
            className={form.input}
            type="text"
            value={name}
            placeholder="e.g. Spelling"
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className={`${form.button} ${form.buttonPrimary}`}
          disabled={!name.trim() || add.isPending}
        >
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
    </div>
  );
}

// ---------- Grid cells ----------

function SubjectRow({
  classroomId,
  weekStartIso,
  subject,
  dates,
  assignments,
}: {
  classroomId: string;
  weekStartIso: string;
  subject: TurnInSubjectDto;
  dates: Date[];
  assignments: TurnInAssignmentDto[];
}) {
  const api = useApi();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (v: { date: string; assignment: string }) =>
      api.planner.putAssignment(classroomId, subject.id, v.date, v.assignment),
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: ['planner-assignments', classroomId, weekStartIso],
      }),
  });

  const valueFor = (dateIso: string) =>
    assignments.find((a) => a.subjectId === subject.id && a.date === dateIso)?.assignment ?? '';

  return (
    <>
      <div className={`${styles.cell} ${styles.rowLabel}`}>{subject.name}</div>
      {dates.map((d, i) => {
        const dateIso = isoOf(d);
        const current = valueFor(dateIso);
        return (
          <div key={i} className={styles.cell}>
            <input
              key={`${subject.id}:${dateIso}:${current}`}
              className={styles.cellInput}
              defaultValue={current}
              maxLength={200}
              aria-label={`${subject.name} assignment for ${dateIso}`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== current) save.mutate({ date: dateIso, assignment: v });
              }}
            />
          </div>
        );
      })}
    </>
  );
}

function TodoCell({
  classroomId,
  weekStartIso,
  dateIso,
  days,
}: {
  classroomId: string;
  weekStartIso: string;
  dateIso: string;
  days: TodoDayDto[];
}) {
  const api = useApi();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (lines: string[]) => api.planner.putTodoDay(classroomId, dateIso, lines),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['planner-todo', classroomId, weekStartIso] }),
  });

  const current = days.find((d) => d.date === dateIso)?.lines ?? [];
  const currentText = current.join('\n');

  return (
    <div className={styles.cell}>
      <textarea
        key={`${dateIso}:${currentText}`}
        className={styles.cellTextarea}
        defaultValue={currentText}
        rows={3}
        placeholder="One task per line"
        aria-label={`To-do for ${dateIso}`}
        onBlur={(e) => {
          const lines = e.target.value
            .split('\n')
            .map((l) => l.trim())
            .filter((l) => l.length > 0);
          if (lines.join('\n') !== currentText) save.mutate(lines);
        }}
      />
    </div>
  );
}
